import * as React from "react";
import {AccountBalanceResult, DashboardUser, ListRequest} from "../../../../generated/protocol";
import {PagerModel} from "../../list/PagerModel";
import {EntityDataTable} from "../../list/EntityDataTable";
import {AppCtx} from "../../../ioc/ApplicationContext";
import {ListUtils} from "../../list/ListUtils";
import {LoadingViewState} from "../RouterView";
import {Form, FormListener} from "../../form/Form";
import {FormGroup} from "../../form/FormGroup";
import {FormInput} from "../../form/FormInput";
import {createInitialState, ListView} from "../ListView";
import {SelectInput} from "../../form/SelectInput";
import * as _ from "lodash";
import {DASHBOARD_USER_SERVICE_NAME, DashboardUserService} from "../../../service/DashboardUserService";
import {WALLET_SERVICE_NAME, WalletService, WalletTransactionRequest} from "../../../service/admin/WalletService";
import {MASS_UPDATE_SERVICE_NAME, MassUpdateItem, MassUpdateService} from "../../../service/admin/MassUpdateService";
import {USER_SERVICE_NAME, UserService} from "../../../service/admin/UserService";
import { reasonCategoryConfig } from '../../dropdown-options';

interface FileReaderEventTarget extends EventTarget {
    result: string;
}

interface FileReaderEvent extends Event {
    target: FileReaderEventTarget;

    getMessage(): string;
}

interface MassUpdateListRequest extends ListRequest {
    userId: string,
    currencyCode: string,
    status: string
}

interface MassUpdateFormModel {
    userId: string,
    currencyCode: string,
    status: string,
    transactionType: string,
    reason: string,
}

interface State extends LoadingViewState {
    listRequest?: MassUpdateListRequest;
    form?: MassUpdateFormModel;
    csvFileName?: string;
    currencies?: string[];
    statuses?: string[];
    transactionTypes?: string[];
    validationMap?: any;
    uploading: boolean;
}

export class MassUpdateView extends ListView<State, MassUpdateListRequest> implements FormListener<MassUpdateFormModel> {
    columns = [
        {property: "id", name: "id", orderBy: "id"},
        {
            property: "status",
            name: "status",
            orderBy: "status",
            renderer: (property, columnName, item: MassUpdateItem) => {
                return (item.status != "ERROR" ?
                    <div>{item.status}</div> :
                    <div style={{color: "red", fontWeight: "bold"}}>{item.status}
                        <div style={{fontSize: "75%"}}>{item.errorMessage}</div>
                    </div>)
            }
        },
        {property: "userId", name: "User Id", orderBy: "userId"},
        {property: "currencyCode", name: "Currency", orderBy: "currencyCode"},
        {property: "amount", name: "Amount", orderBy: "amount"},
        {property: "comment", name: "Comment", orderBy: "comment"}
    ];
    actions = [
        {
            id: "view", name: "View", className: "btn btn-primary", iconClassName: "fa fa-ban", filter: (item) => {
                return item.status === "COMPLETE"
            }
        },
        {
            id: "update", name: "Update", className: "btn btn-warning", iconClassName: "fa fa-ban", filter: (item) => {
                return item.status === "PENDING"
            }
        }
    ];

    itemService: MassUpdateService;

    walletService: WalletService;

    userService: UserService;

    dashboardUserService: DashboardUserService;

    constructor(props) {
        super(props, "/mass-update-accounts");

        this.itemService = AppCtx.resolve<MassUpdateService>(MASS_UPDATE_SERVICE_NAME);
        this.walletService = AppCtx.resolve<WalletService>(WALLET_SERVICE_NAME);
        this.userService = AppCtx.resolve<UserService>(USER_SERVICE_NAME);
        this.dashboardUserService = AppCtx.resolve<DashboardUserService>(DASHBOARD_USER_SERVICE_NAME);

        this.state.currencies = ["ANY", "XSD", "XGC", "VAULT-GC", "VAULT-SC"];
        this.state.statuses = ["ANY", "PENDING", "COMPLETE", "ERROR"];
        this.state.transactionTypes = ["ADDITION", "REMOVAL"];
        this.state.uploading = false;

        this.state = createInitialState({limit: 50, orderBy: "id"}, this.state);
    }

    setEntities(entities: MassUpdateItem[]) {
        this.itemService.setEntities(entities);
        let anyRequest = _.clone(this.state.listRequest);
        anyRequest.offset = 0;
        anyRequest.orderBy = "id";
        anyRequest.ascending = true;
        anyRequest.userId = "";
        anyRequest.currencyCode = "ANY";
        anyRequest.status = "ANY";

        this.forceSearch(anyRequest);
    }

    doSearch() {
        let listRequest = _.clone(this.state.listRequest);
        listRequest.offset = 0;
        listRequest.userId = this.state.form.userId;
        listRequest.currencyCode = this.state.form.currencyCode;
        listRequest.status = this.state.form.status;

        this.forceSearch(listRequest);
    }

    forceSearch(listRequest: MassUpdateListRequest) {
        this.setState(({listRequest: listRequest, form: {
                ...listRequest,
                transactionType: this.state.form.transactionType,
                reason: this.state.form.reason,
            }} as any), () => {
            (this.refs["dataTable"] as EntityDataTable).doSearch();
        })
    }

    loadContent(): Promise<any> {
        return undefined;
    }

    formDidUpdate(formModel: MassUpdateFormModel, validationMap?: any) {
        this.setState({"form": formModel, validationMap: validationMap});
    }

    pagerChanged(pager: PagerModel) {
        let listRequest = ListUtils.toListRequest(this.state.listRequest, pager);
        this.setState({listRequest: listRequest});
    }

    onSubmitError(): void {

    };

    onSubmit(): void {
        this.doSearch();
    };

    private processUpdateItem(self, item: MassUpdateItem, dashboardUser: DashboardUser, onComplete: () => void) {
        if (item.status != "PENDING" || !self.state.form.reason) {
            onComplete();
            return;
        }
        self.userService.getEntity(item.userId).then(user => {
            const amount = parseFloat(item.amount);
            const accountType = getWalletAccountTypeFromCurrencyCode(item.currencyCode);
            const comment = item.comment + "( updated by: " + dashboardUser.username + ")";

            const request: WalletTransactionRequest = {
                userId: +user.externalUserId,
                amount: '' + Math.abs(amount),
                comment,
                accountType,
                reasonCode: self.state.form.reason,
            };
            const responsePromise = amount > 0 ? self.walletService.credit(request) : self.walletService.debit(request);
            return responsePromise.then(balanceResult => {
                return {
                    ...balanceResult,
                    cubeiaId: user.userId,
                    walletAccountType: accountType,
                    pokUserId: user.externalUserId,
                }
            });
        })
            .then((result) => {
                item.walletAccountType = result.walletAccountType;
                item.pokUserId = result.pokUserId;
                item.status = "COMPLETE";
                onComplete();
            })
            .catch(e => {
                item.status = "ERROR";
                item.errorMessage = e.message;
                onComplete();
            });
    }

    updateAll() {
        if (this.state.uploading) return
        let self = this;
        let clonedItems: MassUpdateItem[] = Object.assign([], this.itemService.getFilteredEntities(this.state.listRequest).filter(item => item.status == "PENDING"));

        let action = this.state.form.transactionType === "ADDITION" ? "credit" : "debit";
        const confirmed = confirm(`You are about to mass ${action} ${clonedItems.length} transactions. Would you like to continue?`)
        if (!confirmed) return
        this.setState({ uploading: true })
        this.dashboardUserService.loadUserInfo().then((dashboardUser: DashboardUser) => {
            function processNextUpdate() {
                self.processUpdateItem(self, clonedItems.shift(), dashboardUser, () => {
                    self.doSearch();
                    if (clonedItems.length > 0) {
                        processNextUpdate();
                    } else {
                        self.setState({ uploading: false })
                    }
                });
            }

            processNextUpdate();
        });
    }

    handleFileUpload(event) {
        let self = this;
        let file = event.target.files[0];
        if (!file) {
            return;
        }
        let reader = new FileReader();
        let entities = [];
        let invalidAdditionCount = 0;
        let invalidRemovalCount = 0;
        reader.onload = function (e: FileReaderEvent) {
            let lines = e.target.result.split(/\n/);

            lines.forEach((csvLine: string, index: number) => {
                let fields: string[] = csvLine.split(/,/);
                if (fields.length != 4) {
                    console.log("Malformed line", csvLine);
                    return;
                }
                const amount = parseFloat(fields[2]);
                if (this.state.form.transactionType === "ADDITION" && amount < 0) {
                    invalidAdditionCount++;
                    return;
                } else if (this.state.form.transactionType === "REMOVAL" && amount > 0) {
                    invalidRemovalCount++;
                    return;
                }

                entities.push({
                    id: index,
                    status: "PENDING",
                    userId: parseInt(fields[0]),
                    currencyCode: fields[1],
                    amount: amount,
                    comment: fields[3]
                })
            });
            if (invalidRemovalCount > 0) {
                alert(`Expected all transactions to be removals but detected ${invalidRemovalCount} positive amount/s`);
            } else if (invalidAdditionCount > 0) {
                alert(`Expected all transactions to be additions but detected ${invalidAdditionCount} negative amount/s`);
            }
            const invalidCsv = invalidAdditionCount > 0 || invalidRemovalCount > 0
            this.setState({
                ...this.state,
                csvFileName: file.name,
            }, () => {
                self.setEntities(invalidCsv ? [] : entities);
            });
        }.bind(this);
        reader.readAsText(file);
    }

    getTitle() {
        return "Mass Update";
    }

    renderStats() {
        let stats: any = {};
        let currencies: string[] = Object.assign([], this.state.currencies)

        currencies.forEach((currencyCode) => {
            if (currencyCode == "ANY") return;
            this.state.statuses.forEach((status) => {
                if (status == "ANY") return;
                stats[currencyCode] = stats[currencyCode] || {};
                stats[currencyCode][status] = {count: 0, debit: 0, credit: 0};
            });
        });

        this.itemService.getEntities().forEach((entity) => {
            let amount = parseFloat(entity.amount);
            if (typeof stats[entity.currencyCode] == "undefined") {
                if (!stats["BAD"]) {
                    currencies.push("BAD");
                    stats["BAD"] = stats["BAD"] || {};
                    stats["BAD"]["ERROR"] = stats["BAD"]["ERROR"] || {count: 0, debit: 0, credit: 0};
                }
                if (amount > 0) {
                    stats["BAD"]["ERROR"].credit += amount;
                }
                else {
                    stats["BAD"]["ERROR"].debit += amount;
                }
                stats["BAD"]["ERROR"].count++;

                entity.status = "ERROR";
                entity.errorMessage = "Unknown currency code";
            }
            else {
                if (amount > 0) {
                    stats[entity.currencyCode][entity.status].credit += amount;
                }
                else {
                    stats[entity.currencyCode][entity.status].debit += amount;
                }
                stats[entity.currencyCode][entity.status].count++;
            }
        });

        return currencies.map((currencyCode) => {
            return (
                currencyCode == "ANY" ?
                    "" :
                    <div className="ibox-content">
                        <h3 style={{padding: "0px", margin: "0px"}}>{currencyCode}</h3>
                        <div>
                            {this.state.statuses.map((status) => {
                                return (
                                    status == "ANY" || (!stats[currencyCode][status]) || stats[currencyCode][status].count == 0 ?
                                        "" :
                                        <div><span
                                            style={{marginLeft: "20px"}}>{status}</span> : <span> {stats[currencyCode][status].count} ( Totals: {stats[currencyCode][status].credit}/{stats[currencyCode][status].debit} )</span>
                                        </div>);
                            })}
                        </div>
                    </div>
            );
        });
    }

    renderContent() {
        const csvUploaded = this.itemService.getEntities().length > 0 && !!this.state.csvFileName;
        const requiredFieldsFilled = !!this.state.form.transactionType && !!this.state.form.reason
        const allowCsvUpload = requiredFieldsFilled;
        const showTableAndFilters = csvUploaded && requiredFieldsFilled

        const reasonCategoriesForSelection = reasonCategoryConfig[this.state.form.transactionType] || {}
        return (
            <div>
                <div className="row wrapper border-bottom white-bg page-heading">
                    <div className="col-lg-12">
                        <h1>{this.getTitle()}</h1>
                    </div>
                </div>
                <div className="wrapper wrapper-content animated fadeInRight">

                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h5>Filter Accounts</h5>
                        </div>
                        <div className="ibox-content">
                            <div className="ibox-content">
                                <Form className="form-horizontal" formListener={this} modelObject={this.state.form}
                                      validationMap={this.state.validationMap}>

                                    <FormGroup className="form-group" model="transactionType" style={{display: "block"}}>
                                        <label className="col-lg-2 control-label">Transaction Type</label>
                                        <div className="col-lg-10">
                                            <SelectInput className="form-control" model="transactionType"
                                                         values={this.state.transactionTypes}
                                                         disabled={showTableAndFilters}
                                            />
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="form-group" model="reason" style={{display: "block"}}>
                                        <label className="col-lg-2 control-label">Reason</label>
                                        <div className="col-lg-10">
                                            <SelectInput className="form-control" model="reason"
                                                         values={Object.keys(reasonCategoriesForSelection)}
                                                         displayValue={(v) => reasonCategoriesForSelection[v].reasonDisplay}
                                                         disabled={showTableAndFilters}
                                            />
                                        </div>
                                    </FormGroup>

                                    <FormGroup className="form-group" model="csvFileName"
                                               onChange={this.handleFileUpload.bind(this)}>
                                        <label className="col-sm-2 control-label">Upload CSV:</label>
                                        <div className="col-sm-10">
                                            <FormInput className="form-control" value={this.state.csvFileName}
                                                       type="file" accept=".csv" disabled={!allowCsvUpload}/>
                                        </div>
                                    </FormGroup>


                                    <FormGroup className="form-group" model="userId"
                                               style={{display: showTableAndFilters ? "block" : "none"}}>
                                        <label className="col-lg-2 control-label">User ID</label>
                                        <div className="col-lg-10">
                                            <FormInput className="form-control" model="userId" type="text"/>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="form-group" model="currencyCode"
                                               style={{display: showTableAndFilters ? "block" : "none"}}>
                                        <label className="col-lg-2 control-label">Currency</label>
                                        <div className="col-lg-10">
                                            <SelectInput className="form-control" model="currencyCode"
                                                         values={this.state.currencies}/>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="form-group" model="status"
                                               style={{display: showTableAndFilters ? "block" : "none"}}>
                                        <label className="col-lg-2 control-label">State</label>
                                        <div className="col-lg-10">
                                            <SelectInput className="form-control" model="status"
                                                         values={this.state.statuses}/>
                                        </div>
                                    </FormGroup>

                                    <div className="form-group" style={{display: showTableAndFilters ? "block" : "none"}}>
                                        <div className="col-lg-offset-2 col-lg-10">
                                            <input className="btn btn-primary" value="List Items" type="submit"/>
                                        </div>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>

                    <div className="ibox float-e-margins" style={{display: showTableAndFilters ? "block" : "none"}}>
                        <div className="ibox-title">
                            <h5>Statistics</h5>
                        </div>
                        {this.renderStats()}
                    </div>

                    <div style={{display: showTableAndFilters ? "block" : "none"}}>
                        <EntityDataTable ref="dataTable" service={this.itemService}
                                     onPagerChange={this.pagerChanged.bind(this)} columns={this.columns}
                                     listRequest={this.state.listRequest}/>
                    </div>

                    <div className="col-lg-offset-10 col-lg-10" style={{display: showTableAndFilters ? "block" : "none"}}>
                        <input className="btn btn-danger" value="Update Filtered" type="submit"
                               onClick={this.updateAll.bind(this)} disabled={this.state.uploading}/>
                    </div>
                </div>
            </div>
        );
    }

}

function getWalletAccountTypeFromCurrencyCode(currencyCode: string): string {
    switch (currencyCode) {
        case 'XSD': return 'sc-main';
        case 'XGC': return 'gc-main';
        case 'VAULT-SC': return 'sc-vault';
        case 'VAULT-GC': return 'gc-vault';
        default: throw new Error("Unknown currency code " + currencyCode);
    }
}


// WEBPACK FOOTER //
// ./src/app/ui/view/account/MassUpdateView.tsx